/** KPI «Аналитики ОПТ» с Δ к периоду сравнения (сетка `KpiGrid` «Аналитики МП») и Δ в ячейках
 *  дерева. Δ — только у отгрузок; портфель заказов на дату снимка (к отгрузке — в периоде,
 *  просрочено — все) Δ не имеет. Агрегат null — строк нет: отгружено 0 (это факт, а не «нет данных»); маржа
 *  null при строках — «нет цены — не ноль»: Δ по ней не считается. */
import type { Agg } from "@/api/wholesale";
import { delta, deltaTone, type Tone } from "../ozon-analytics/calc";
import { compact, MINUS, NBSP, pct, pctSigned, pp, rub, rubSigned } from "../ozon-analytics/format";
import type { Kpi } from "../ozon-analytics/kpi";
import { marginCell, qty, revenueCell, rubOrDash, type MoneyCell } from "./calc";

const NO_SHIPPED = "нет отгрузок";
/** Порог шума Δ п.п. (в долях): 0,05 п.п., как у «Аналитики МП». */
const PP_EPS = 0.0005;

/** Штуки со знаком для Δ: «+3», «−1,5», «0». */
export function qtySigned(n: number): string {
  if (Math.abs(n) < 0.0005) return "0";
  return `${n > 0 ? "+" : MINUS}${qty(Math.abs(n))}`;
}

const star = (c: MoneyCell): string => (c.partial ? `${c.text}*` : c.text);

interface Metric {
  text: string;
  title: string | null;
  value: number | null;
}

export type Measure = "revenue" | "margin" | "margin_pct" | "quantity";

/** Показатель агрегата для KPI: текст (с «*» у частичной суммы), подсказка, число для Δ. */
function metric(a: Agg | null, what: Measure): Metric {
  if (!a) {
    return what === "margin_pct"
      ? { text: "—", title: NO_SHIPPED, value: null }
      : { text: what === "quantity" ? "0" : NO_SHIPPED, title: null, value: 0 };
  }
  switch (what) {
    case "revenue": {
      const c = revenueCell(a);
      return { text: star(c), title: c.title, value: a.revenue_gross };
    }
    case "margin": {
      const c = marginCell(a);
      return { text: star(c), title: c.title, value: a.net_margin };
    }
    case "margin_pct": {
      const c = marginCell(a);
      return { text: c.pct, title: c.title, value: a.margin_pct };
    }
    case "quantity":
      return { text: qty(a.quantity), title: null, value: a.quantity };
  }
}

function kpi(
  title: string,
  hint: string,
  cur: Metric,
  was: Metric | undefined,
  fmt: "rub" | "qty" | "pp",
  note: string | null = null,
): Kpi {
  const hasCmp = was !== undefined;
  const d = hasCmp && cur.value !== null && was.value !== null ? delta(cur.value, was.value) : null;
  const abs = d === null ? "—" : fmt === "rub" ? rubSigned(d.abs) : fmt === "qty" ? qtySigned(d.abs) : pp(d.abs);
  return {
    title,
    cur: cur.text,
    curTitle: cur.title,
    was: hasCmp ? was.text : null,
    wasTitle: hasCmp ? was.title : null,
    deltaAbs: hasCmp ? abs : null,
    deltaRel: d && fmt !== "pp" ? pctSigned(d.rel) : null,
    tone: deltaTone(d?.abs, fmt === "pp" ? PP_EPS : 0.5),
    hint,
    note,
  };
}

/** KPI отгрузок периода: выручка, маржа, маржа %, штуки; `cmp` — отгрузки периода сравнения
 *  (undefined — сравнения нет, null — отгрузок в нём не было). */
export function shippedKpis(cur: Agg | null, cmp: Agg | null | undefined): Kpi[] {
  const was = (what: Measure): Metric | undefined => (cmp === undefined ? undefined : metric(cmp, what));
  return [
    kpi(
      "Выручка",
      `с НДС, как в 1С${cur ? ` · без НДС ${rubOrDash(cur.revenue_net)}` : ""}`,
      metric(cur, "revenue"),
      was("revenue"),
      "rub",
      cur ? null : "отгрузок в периоде нет",
    ),
    kpi("Маржа", "выручка − себестоимость − склад, по строкам с ценой", metric(cur, "margin"), was("margin"), "rub"),
    kpi("Маржа, % выручки", "маржа / выручка строк с ценой", metric(cur, "margin_pct"), was("margin_pct"), "pp"),
    kpi("Отгружено, шт", "штуки отгрузок (базовые единицы 1С)", metric(cur, "quantity"), was("quantity"), "qty"),
  ];
}

// ─────────────── Δ в ячейке дерева ───────────────
export interface DeltaPart {
  text: string;
  tone: Tone;
  title: string;
}

/** Деньги компактно со знаком: «+1,2 млн ₽», «−45 тыс ₽». */
function compactSigned(n: number): string {
  const c = compact(n);
  return `${n > 0 ? "+" : ""}${c}${NBSP}₽`;
}

/** Значение коротко (ячейка) и полностью (подсказка). */
const short = (v: number, what: Measure): string =>
  what === "quantity" ? qty(v) : what === "margin_pct" ? pct(v) : `${compact(v)}${NBSP}₽`;
const full = (v: number, what: Measure): string =>
  what === "quantity" ? `${qty(v)} шт` : what === "margin_pct" ? pct(v) : rub(v);

/** Вторая строка ячейки дерева — Δ к периоду сравнения. `cur` / `cmp` — агрегаты отгрузок узла
 *  (null — отгрузок нет). Узел новый — «нов.»; ушёл — «было …»; значения нет у одной стороны (нет
 *  цены) — Δ нет (null). */
export function treeDelta(cur: Agg | null, cmp: Agg | null, what: Measure): DeltaPart | null {
  if (!cur && !cmp) return null;
  if (!cmp) return { text: "нов.", tone: "neutral", title: "в периоде сравнения отгрузок не было" };
  const w = metric(cmp, what).value;
  if (w === null) return null;
  if (!cur) {
    return { text: `было ${short(w, what)}`, tone: "bad", title: `в периоде отгрузок нет; в сравнении — ${full(w, what)}` };
  }
  const c = metric(cur, what).value;
  if (c === null) return null;
  const d = delta(c, w)!;
  if (what === "margin_pct") {
    return { text: pp(d.abs), tone: deltaTone(d.abs, PP_EPS), title: `было ${pct(w)}, Δ ${pp(d.abs)}` };
  }
  const abs = what === "quantity" ? `${qtySigned(d.abs)} шт` : rubSigned(d.abs);
  const text = d.rel !== null ? pctSigned(d.rel) : what === "quantity" ? qtySigned(d.abs) : compactSigned(d.abs);
  return {
    text,
    tone: deltaTone(d.abs, 0.5),
    title: `было ${full(w, what)}, Δ ${abs}${d.rel !== null ? ` (${pctSigned(d.rel)})` : ""}`,
  };
}
